/**
 * OtpChallengeDialog — modal for verifying a sensitive profile change.
 *
 * Built without Radix (Dialog isn't enabled in @finmin/ui yet) — uses a
 * portal-free overlay that locks scroll and traps Escape.
 *
 * Lifecycle:
 *   - Mounts when the parent's update hook reports `phase === 'awaiting-otp'`.
 *   - Renders a 6-digit OTP input + Verify button.
 *   - On `onSubmit(otp)`, parent re-submits the held payload + otp.
 *   - On `onCancel`, parent drops the pending payload.
 */
import * as React from 'react';
import { Button, OtpInput, Spinner } from '@finmin/ui';
import { X, Shield } from 'lucide-react';

interface OtpChallengeDialogProps {
  open:            boolean;
  /** Backend-echoed masked target (mobile or email), or null. */
  otpTarget:       string | null;
  /** Expiry seconds from the challenge response. */
  expiresInSec:    number;
  isVerifying:     boolean;
  errorMessage?:   string | null;
  onSubmit:        (otp: string) => void | Promise<void>;
  onCancel:        () => void;
}

export function OtpChallengeDialog({
  open,
  otpTarget,
  expiresInSec,
  isVerifying,
  errorMessage,
  onSubmit,
  onCancel,
}: OtpChallengeDialogProps) {
  const [otp, setOtp]               = React.useState('');
  const [remaining, setRemaining]   = React.useState(expiresInSec);

  // Reset every time the dialog opens.
  React.useEffect(() => {
    if (open) {
      setOtp('');
      setRemaining(expiresInSec);
    }
  }, [open, expiresInSec]);

  // Countdown.
  React.useEffect(() => {
    if (!open) return;
    const t = setInterval(() => setRemaining((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [open]);

  // Escape closes.
  React.useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onCancel();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onCancel]);

  if (!open) return null;

  const mm = Math.floor(remaining / 60);
  const ss = String(remaining % 60).padStart(2, '0');
  const isExpired = remaining <= 0;

  const targetLabel = otpTarget
    ? otpTarget.includes('@')
      ? `email ending ${otpTarget}`
      : `mobile ${otpTarget}`
    : 'your registered contact';

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (otp.length === 6 && !isVerifying) void onSubmit(otp);
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="otp-dialog-title"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-foreground/40 backdrop-blur-sm"
        onClick={onCancel}
        aria-hidden
      />

      {/* Card */}
      <div className="relative w-full max-w-sm rounded-2xl border border-border bg-background p-6 shadow-xl">
        <button
          type="button"
          onClick={onCancel}
          className="absolute right-3 top-3 rounded-md p-1.5 text-muted-foreground hover:bg-muted transition-colors focus:outline-none focus:ring-2 focus:ring-ring"
          aria-label="Close"
        >
          <X className="h-4 w-4" aria-hidden />
        </button>

        <div className="flex items-start gap-3 mb-4">
          <div
            className="rounded-full p-2 shrink-0"
            style={{ backgroundColor: 'hsl(var(--palette-mint, 152 41% 92%))' }}
          >
            <Shield
              className="h-5 w-5"
              style={{ color: 'hsl(var(--palette-forest, 176 72% 17%))' }}
              aria-hidden
            />
          </div>
          <div>
            <h2 id="otp-dialog-title" className="text-base font-semibold text-foreground">
              Verify it's you
            </h2>
            <p className="text-sm text-muted-foreground mt-0.5">
              We sent a 6-digit code to your {targetLabel}.
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <OtpInput
            value={otp}
            onChange={setOtp}
            disabled={isVerifying}
            error={Boolean(errorMessage)}
            label="Enter the 6-digit code"
            onComplete={(v) => {
              if (!isVerifying) void onSubmit(v);
            }}
          />

          {errorMessage && (
            <p className="text-xs text-destructive">{errorMessage}</p>
          )}

          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>
              {isExpired
                ? <span className="text-destructive">Code expired</span>
                : `Expires in ${mm}:${ss}`}
            </span>
          </div>

          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              className="flex-1"
              disabled={isVerifying}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex-1"
              disabled={otp.length !== 6 || isVerifying || isExpired}
            >
              {isVerifying ? (
                <span className="inline-flex items-center gap-2">
                  <Spinner className="h-3.5 w-3.5" /> Verifying…
                </span>
              ) : (
                'Verify'
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
